<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">{{ $t('golf_reservations') }}</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" rounded elevation="2" @click="openDialog()">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('new_reservation') }}
          </v-btn>
        </v-card-title>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-calendar</v-icon>
            {{ $t('calendar_view') }}
          </v-tab>
          <v-tab>
            <v-badge color="red" :content="pendingRequestsCount.toString()" :value="pendingRequestsCount > 0" overlap>
              <v-icon left>mdi-bell-ring</v-icon>
              {{ $t('requests') }}
            </v-badge>
          </v-tab>
          <v-tab>
            <v-badge color="red" :content="cancellationRequestsCount.toString()" :value="cancellationRequestsCount > 0"
              overlap>
              <v-icon left>mdi-cancel</v-icon>
              {{ $t('cancellations') }}
            </v-badge>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Calendar View -->
          <v-tab-item>
            <v-card class="fill-height" elevation="0">
              <v-toolbar flat color="white" class="mb-2">
                <v-btn outlined class="mr-4" color="grey darken-2" @click="setToday">
                  {{ $t('today') }}
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="prev">
                  <v-icon small>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="next">
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-btn>
                <v-toolbar-title v-if="$refs.calendar">
                  {{ $refs.calendar.title }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-sheet height="700">
                <v-calendar ref="calendar" v-model="focus" color="primary" type="month" locale="es-mx"
                  @click:date="openDayDialog" @change="updateRange">
                  <template v-slot:day="{ date }">
                    <div class="d-flex justify-center align-center fill-height"
                      style="width: 100%; height: 100%; cursor: pointer; background-color: transparent;"
                      @click="openDayDialog({ date })" v-ripple>
                      <v-chip v-if="getReservationCount(date) > 0" color="primary" small class="font-weight-bold">
                        {{ getReservationCount(date) }}
                      </v-chip>
                    </div>
                  </template>
                </v-calendar>
              </v-sheet>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Requests View -->
          <v-tab-item>
            <v-card elevation="3" class="rounded-lg">
              <v-data-table :headers="headers" :items="pendingReservations" :loading="loadingRequests"
                class="elevation-0" :no-data-text="$t('no_records_yet')">
                <template #item.course="{ item }">
                  <v-chip small label color="blue lighten-5" text-color="blue darken-3">
                    {{ getCourseName(item.course) }}
                  </v-chip>
                </template>
                <template #item.invited_people="{ item }">
                  <div v-if="item.invited_people && item.invited_people.length > 0">
                    <v-chip v-for="(person, index) in item.invited_people" :key="index" x-small class="mr-1 mb-1">
                      {{ person }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text caption font-italic">Sin jugadores</span>
                </template>
                <template #item.hour="{ item }">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  <strong>{{ item.hour }}</strong>
                </template>
                <template #item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template #item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" dark small>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template #item.display_user="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ getInitials(item.display_user) }}</span>
                    </v-avatar>
                    {{ item.display_user }}
                  </div>
                </template>
                <template #item.actions="{ item }">
                  <div class="d-flex">
                    <!-- Pending Actions -->
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="green" v-bind="attrs" v-on="on" @click="changeStatus(item, 2)">
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('confirm_reservation') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="red" v-bind="attrs" v-on="on" @click="changeStatus(item, 5)">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('reject_reservation') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Cancellations View -->
          <v-tab-item>
            <v-card elevation="3" class="rounded-lg">
              <v-data-table :headers="headers" :items="cancellationReservations" :loading="loadingRequests"
                class="elevation-0" :no-data-text="$t('no_records_yet')">
                <template #item.course="{ item }">
                  <v-chip small label color="blue lighten-5" text-color="blue darken-3">
                    {{ getCourseName(item.course) }}
                  </v-chip>
                </template>
                <template #item.invited_people="{ item }">
                  <div v-if="item.invited_people && item.invited_people.length > 0">
                    <v-chip v-for="(person, index) in item.invited_people" :key="index" x-small class="mr-1 mb-1">
                      {{ person }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text caption font-italic">Sin jugadores</span>
                </template>
                <template #item.hour="{ item }">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  <strong>{{ item.hour }}</strong>
                </template>
                <template #item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template #item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" dark small>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template #item.display_user="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ getInitials(item.display_user) }}</span>
                    </v-avatar>
                    {{ item.display_user }}
                  </div>
                </template>
                <template #item.actions="{ item }">
                  <div class="d-flex">
                    <!-- Cancellation Requested Actions -->
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="green" v-bind="attrs" v-on="on"
                          @click="handleCancellationRequest(item, 'approve')">
                          <v-icon>mdi-check-all</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('accept_cancellation') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="orange" v-bind="attrs" v-on="on"
                          @click="handleCancellationRequest(item, 'reject')">
                          <v-icon>mdi-undo</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('reject_cancellation') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Day Reservations Dialog -->
    <v-dialog v-model="dayDialog" max-width="1000px">
      <v-card>
        <v-toolbar color="primary" dark flat dense>
          <v-toolbar-title class="subtitle-1">
            <v-icon left>mdi-calendar-check</v-icon>
            {{ $t('reservations_for') }} {{ formatDate(filterDate) }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dayDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <div class="d-flex justify-space-between align-center mb-4">
            <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('search')" single-line hide-details
              dense outlined style="max-width: 300px;"></v-text-field>
            <v-btn color="primary" @click="openDialog()">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('new_reservation') }}
            </v-btn>
          </div>

          <v-simple-table class="tee-sheet-table" fixed-header height="600px">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center primary white--text"
                    style="width: 100px; position: sticky; top: 0; z-index: 2;">
                    <v-icon small color="white" class="mr-1">mdi-clock-outline</v-icon>
                    {{ $t('horario') || 'Horario' }}
                  </th>
                  <th class="text-center primary white--text" style="position: sticky; top: 0; z-index: 2;">
                    <v-icon small color="white" class="mr-1">mdi-flag-variant</v-icon>
                    {{ $t('hole') || 'Hoyo' }} 1
                  </th>
                  <th class="text-center primary white--text" style="position: sticky; top: 0; z-index: 2;">
                    <v-icon small color="white" class="mr-1">mdi-flag-variant</v-icon>
                    {{ $t('hole') || 'Hoyo' }} 10
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="time in timeSlots" :key="time" class="tee-row">
                  <td class="text-center font-weight-bold grey lighten-4">
                    {{ time }}
                    <div v-if="isWeekend" class="caption green--text font-weight-bold mt-1" style="line-height: 1;">
                      <small>R: {{ getRecessTime(time) }}</small>
                    </div>
                  </td>

                  <!-- Salida 1 -->
                  <td class="pa-1 cell-hover" style="vertical-align: top; min-width: 250px; position: relative;">
                    <div class="d-flex flex-column fill-height">
                      <div v-for="res in getReservationsBySlot(time, 1)" :key="res.id" class="mb-1">
                        <v-card outlined
                          class="pa-2 d-flex align-center justify-space-between transition-swing clickable-card"
                          :color="getStatusColor(res.status) + ' lighten-5'" style="border-left: 4px solid;"
                          :style="{ borderLeftColor: getStatusColor(res.status) }" @click="showReservationDetails(res)">
                          <div class="d-flex flex-column overflow-hidden" style="width: 100%;">
                            <div class="d-flex align-center">
                              <v-avatar size="24" color="primary" class="white--text mr-2">
                                <span class="caption" style="font-size: 10px;">{{ getInitials(res.display_user)
                                }}</span>
                              </v-avatar>
                              <div class="font-weight-bold caption text-truncate">
                                {{ res.display_user }}
                              </div>
                            </div>
                            <div class="d-flex align-center mt-1">
                              <v-chip x-small label outlined class="mr-1">
                                {{ res.invited_people ? res.invited_people.length : 0 }} jug.
                              </v-chip>
                              <v-chip x-small :color="getStatusColor(res.status)" dark>
                                {{ getStatusText(res.status) }}
                              </v-chip>
                            </div>
                          </div>

                          <div class="d-flex ml-1">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="editItem(res)"
                                  :disabled="isActionDisabled(res, 'edit')">
                                  <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small color="red" v-bind="attrs" v-on="on"
                                  @click.stop="confirmCancel(res)" :disabled="isActionDisabled(res, 'cancel')">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('cancel_reservation') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmNoShow(res)"
                                  :disabled="isActionDisabled(res, 'no_show')">
                                  <v-icon small>mdi-account-cancel</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('mark_as_no_show') }}</span>
                            </v-tooltip>
                          </div>
                        </v-card>
                      </div>

                      <!-- Add Button -->
                      <div class="add-btn-container d-flex justify-center align-center"
                        style="min-height: 30px; opacity: 0.2; transition: opacity 0.2s;"
                        @click="openDialogWithTime(time, 1)">
                        <v-btn icon small color="primary">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </td>

                  <!-- Salida 10 -->
                  <td class="pa-1 cell-hover" style="vertical-align: top; min-width: 250px; position: relative;">
                    <div class="d-flex flex-column fill-height">
                      <div v-for="res in getReservationsBySlot(time, 10)" :key="res.id" class="mb-1">
                        <v-card outlined
                          class="pa-2 d-flex align-center justify-space-between transition-swing clickable-card"
                          :color="getStatusColor(res.status) + ' lighten-5'" style="border-left: 4px solid;"
                          :style="{ borderLeftColor: getStatusColor(res.status) }" @click="showReservationDetails(res)">
                          <div class="d-flex flex-column overflow-hidden" style="width: 100%;">
                            <div class="d-flex align-center">
                              <v-avatar size="24" color="primary" class="white--text mr-2">
                                <span class="caption" style="font-size: 10px;">{{ getInitials(res.display_user)
                                }}</span>
                              </v-avatar>
                              <div class="font-weight-bold caption text-truncate">
                                {{ res.display_user }}
                              </div>
                            </div>
                            <div class="d-flex align-center mt-1">
                              <v-chip x-small label outlined class="mr-1">
                                {{ res.invited_people ? res.invited_people.length : 0 }} jug.
                              </v-chip>
                              <v-chip x-small :color="getStatusColor(res.status)" dark>
                                {{ getStatusText(res.status) }}
                              </v-chip>
                            </div>
                          </div>

                          <div class="d-flex ml-1">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="editItem(res)"
                                  :disabled="isActionDisabled(res, 'edit')">
                                  <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small color="red" v-bind="attrs" v-on="on"
                                  @click.stop="confirmCancel(res)" :disabled="isActionDisabled(res, 'cancel')">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('cancel_reservation') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmNoShow(res)"
                                  :disabled="isActionDisabled(res, 'no_show')">
                                  <v-icon small>mdi-account-cancel</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('mark_as_no_show') }}</span>
                            </v-tooltip>
                          </div>
                        </v-card>
                      </div>

                      <!-- Add Button -->
                      <div class="add-btn-container d-flex justify-center align-center"
                        style="min-height: 30px; opacity: 0.2; transition: opacity 0.2s;"
                        @click="openDialogWithTime(time, 10)">
                        <v-btn icon small color="primary">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog Create/Edit -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <span class="headline">{{ formTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="mt-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container grid-list-md class="pa-0">
              <!-- Main Details -->
              <v-subheader class="pl-0 font-weight-bold text-uppercase caption primary--text">
                {{ $t('reservation_details') }}
              </v-subheader>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-combobox v-model="selectedUser" :items="users" :loading="loadingUsers" :search-input="userSearch"
                    @update:search-input="val => { if (typeof val === 'string') userSearch = val }"
                    item-text="full_name" item-value="id" :label="$t('holder') + ' / ' + $t('guest')"
                    :rules="[rules.required]" outlined dense clearable :no-data-text="$t('no_users_found')"
                    prepend-inner-icon="mdi-account-search" hide-details="auto" return-object
                    @change="onUserSelectionChange">
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-menu v-model="menuDateForm" :close-on-content-click="false" :nudge-right="40"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.date" :label="$t('reservation_date')"
                        prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" :rules="[rules.required]"
                        outlined dense></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem.date" @input="menuDateForm = false" locale="es-mx"
                      :min="today"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuTime" v-model="menuTime" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.hour" transition="scale-transition" offset-y max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.hour" :label="$t('reservation_time')"
                        prepend-inner-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                        :rules="[rules.required]" outlined dense></v-text-field>
                    </template>
                    <v-time-picker v-if="menuTime" v-model="editedItem.hour" full-width format="24hr"
                      @click:minute="$refs.menuTime.save(editedItem.hour)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.start_hole" :items="[1, 10]" :label="$t('golf_court')"
                    :rules="[rules.required]" outlined dense prepend-inner-icon="mdi-flag-variant"></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.hole_count" :items="[9, 18]" :label="$t('hole_count')"
                    :rules="[rules.required]" outlined dense prepend-inner-icon="mdi-counter"></v-select>
                </v-col>
              </v-row>

              <!-- Players Section -->
              <v-subheader class="pl-0 font-weight-bold text-uppercase caption primary--text mt-2">
                {{ $t('players') }} & {{ $t('caddie') }}
              </v-subheader>

              <!-- Main Player Row -->
              <v-row dense align="center" class="mb-2">
                <v-col cols="12" sm="6">
                  <v-text-field
                    :value="editedItem.user_id ? (users.find(u => u.id === editedItem.user_id)?.full_name || '') : editedItem.guest_name"
                    :label="$t('holder')" outlined dense readonly prepend-inner-icon="mdi-account"
                    hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="mainPlayerCaddie" :items="availableCaddies(mainPlayerCaddie)" item-text="name"
                    item-value="id" :label="$t('caddie')" outlined dense clearable prepend-inner-icon="mdi-account-tie"
                    hide-details @change="updateCaddieIds">
                    <template v-slot:append-outer>
                      <v-btn icon small color="primary" @click="openNewCaddieDialog(-1)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Additional Players Rows -->
              <v-row dense align="center" v-for="(player, index) in editedItem.invited_people" :key="index"
                class="mb-2">
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.invited_people[index]" :label="$t('player') + ' ' + (index + 1)"
                    outlined dense prepend-inner-icon="mdi-account-group" hide-details>
                    <template v-slot:append-outer>
                      <v-btn icon small color="red" @click="removePlayer(index)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="guestCaddies[index]" :items="availableCaddies(guestCaddies[index])"
                    item-text="name" item-value="id" :label="$t('caddie')" outlined dense clearable
                    prepend-inner-icon="mdi-account-tie" hide-details @change="updateCaddieIds">
                    <template v-slot:append-outer>
                      <v-btn icon small color="primary" @click="openNewCaddieDialog(index)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Add Player Input -->
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="newPlayerName" :label="$t('add_player')" outlined dense
                    prepend-inner-icon="mdi-account-plus" @keydown.enter.prevent="addPlayer"
                    :disabled="editedItem.invited_people.length >= 5">
                    <template v-slot:append>
                      <v-btn icon small color="primary" @click="addPlayer"
                        :disabled="!newPlayerName || editedItem.invited_people.length >= 5">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Recurrence Section -->
              <div v-if="editedIndex === -1 && editedItem.user_id" class="mt-4 pa-3 rounded grey lighten-5"
                style="border: 1px solid #e0e0e0;">
                <v-row align="center" dense>
                  <v-col cols="12" sm="4" class="py-0">
                    <v-checkbox v-model="editedItem.is_recurring" :label="$t('is_recurring')" dense hide-details
                      class="mt-0"></v-checkbox>
                  </v-col>
                  <template v-if="editedItem.is_recurring">
                    <v-col cols="12" sm="4" class="py-0">
                      <v-menu v-model="menuEndDateForm" :close-on-content-click="false" :nudge-right="40"
                        transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="editedItem.end_date" :label="$t('end_date')"
                            prepend-inner-icon="mdi-calendar-end" readonly v-bind="attrs" v-on="on"
                            :rules="[rules.required]" outlined dense hide-details></v-text-field>
                        </template>
                        <v-date-picker v-model="editedItem.end_date" @input="menuEndDateForm = false" locale="es-mx"
                          :min="editedItem.date"></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="4" class="py-0">
                      <v-select v-model="editedItem.days_of_week" :items="daysOfWeekOptions" :label="$t('days_of_week')"
                        multiple chips small-chips :rules="[rules.required]" outlined dense hide-details
                        prepend-inner-icon="mdi-calendar-week"></v-select>
                    </v-col>
                  </template>
                </v-row>
              </div>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="close">{{ $t('cancelar') }}</v-btn>
          <v-btn color="primary" @click="save" :disabled="!valid">{{ $t('guardar') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel Confirmation Dialog -->
    <v-dialog v-model="dialogCancel" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('cancel_reservation') }}</v-card-title>
        <v-card-text>{{ $t('confirm_cancel_reservation') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCancel">{{ $t('no') || 'No' }}</v-btn>
          <v-btn color="red darken-1" text @click="cancelItemConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- No Show Confirmation Dialog -->
    <v-dialog v-model="dialogNoShow" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('mark_as_no_show') }}</v-card-title>
        <v-card-text>{{ $t('confirm_no_show') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeNoShow">{{ $t('no') || 'No' }}</v-btn>
          <v-btn color="red darken-1" text @click="noShowItemConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reservation Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="500px">
      <v-card v-if="selectedReservation">
        <v-toolbar :color="getStatusBaseColor(selectedReservation.status)" dark flat>
          <v-toolbar-title>{{ $t('reservation_details') || 'Detalles de Reservación' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-calendar</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('date') }}</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(selectedReservation.date) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-clock-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('time') }}</v-list-item-subtitle>
                <v-list-item-title>{{ selectedReservation.hour }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-golf</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('golf_court') }}</v-list-item-subtitle>
                <v-list-item-title>{{ getCourseName(selectedReservation.course) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-flag-variant</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('start_hole') }}</v-list-item-subtitle>
                <v-list-item-title>{{ selectedReservation.start_hole }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('holder') }}</v-list-item-subtitle>
                <v-list-item-title>{{ selectedReservation.display_user }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-group</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('players') }}</v-list-item-subtitle>
                <v-list-item-title>
                  <div v-if="selectedReservation.invited_people && selectedReservation.invited_people.length > 0">
                    <v-chip v-for="(person, index) in selectedReservation.invited_people" :key="index" small
                      class="mr-1 mb-1">
                      {{ person }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text font-italic">{{ $t('no_players') || 'Sin jugadores' }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-information</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('status') }}</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip :color="getStatusColor(selectedReservation.status)" dark small>
                    {{ getStatusText(selectedReservation.status) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" @click="editItem(selectedReservation); detailsDialog = false"
            :disabled="isActionDisabled(selectedReservation, 'edit')">
            <v-icon left>mdi-pencil</v-icon> {{ $t('edit') }}
          </v-btn>
          <v-btn color="red" dark @click="confirmCancel(selectedReservation); detailsDialog = false"
            :disabled="isActionDisabled(selectedReservation, 'cancel')">
            <v-icon left>mdi-delete</v-icon> {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Caddie Dialog -->
    <v-dialog v-model="dialogNewCaddie" max-width="400px">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          {{ $t('new_caddie') }}
        </v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="formNewCaddie" v-model="validNewCaddie" lazy-validation>
            <v-text-field v-model="newCaddie.name" :label="$t('nombre')" :rules="[rules.required]" outlined
              dense></v-text-field>
            <v-select v-model="newCaddie.caddie_type" :items="caddieTypes" item-text="text" item-value="value"
              label="Tipo" :rules="[rules.required]" outlined dense></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="closeNewCaddieDialog">{{ $t('cancelar') }}</v-btn>
          <v-btn color="primary" @click="saveNewCaddie" :disabled="!validNewCaddie" :loading="loadingNewCaddie">
            {{ $t('guardar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="10000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'GolfReservations',
  data: () => ({
    activeTab: 0,
    loading: false,
    loadingRequests: false,
    loadingUsers: false,
    dialog: false,
    dialogCancel: false,
    dialogNoShow: false,
    detailsDialog: false,
    selectedReservation: null,
    dayDialog: false,
    valid: true,
    menuDateForm: false,
    menuEndDateForm: false,
    menuTime: false,
    focus: new Date().toISOString().substr(0, 10),
    filterDate: new Date().toISOString().substr(0, 10),
    today: new Date().toISOString().substr(0, 10),
    search: '',
    userSearch: null,
    courts: [],
    users: [],
    caddies: [],
    reservations: [],
    monthReservations: [],
    pendingReservations: [],
    cancellationReservations: [],
    reservationEvents: [],
    itemToCancel: null,
    itemToNoShow: null,
    editedIndex: -1,
    editedItem: {
      id: null,
      user_id: null,
      guest_name: '',
      course_id: '',
      caddie_ids: [],
      date: new Date().toISOString().substr(0, 10),
      hour: null,
      start_hole: 1,
      hole_count: 18,
      invited_people: [],
      is_recurring: false,
      end_date: null,
      days_of_week: []
    },
    defaultItem: {
      id: null,
      user_id: null,
      guest_name: '',
      course_id: '',
      caddie_ids: [],
      date: new Date().toISOString().substr(0, 10),
      hour: null,
      start_hole: 1,
      hole_count: 18,
      invited_people: [],
      is_recurring: false,
      end_date: null,
      days_of_week: []
    },
    reservationType: 'resident',
    daysOfWeekOptions: [
      { text: 'Lunes', value: 0 },
      { text: 'Martes', value: 1 },
      { text: 'Miércoles', value: 2 },
      { text: 'Jueves', value: 3 },
      { text: 'Viernes', value: 4 },
      { text: 'Sábado', value: 5 },
      { text: 'Domingo', value: 6 }
    ],
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    mainPlayerCaddie: null,
    guestCaddies: [],
    newPlayerName: '',
    selectedUser: null,
    dialogNewCaddie: false,
    validNewCaddie: true,
    loadingNewCaddie: false,
    newCaddie: {
      name: '',
      caddie_type: null
    },
    caddieCreationContext: null // -1 for main, >= 0 for guest index
  }),

  computed: {
    caddieTypes () {
      return [
        { value: 1, text: this.$t('caddie_type_first') },
        { value: 2, text: this.$t('caddie_type_second') },
        { value: 3, text: this.$t('caddie_type_bag') }
      ]
    },
    formTitle () {
      return this.editedIndex === -1 ? this.$t('new_reservation') : this.$t('edit_reservation')
    },
    headers () {
      return [
        { text: this.$t('reservation_date'), value: 'date', align: 'start' },
        { text: this.$t('reservation_time'), value: 'hour' },
        { text: this.$t('holder'), value: 'display_user' },
        { text: this.$t('golf_court'), value: 'course' },
        { text: this.$t('start_hole'), value: 'start_hole' },
        { text: this.$t('hole_count'), value: 'hole_count' },
        { text: this.$t('players'), value: 'invited_people' },
        { text: this.$t('reservation_status'), value: 'status' },
        { text: this.$t('acciones'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    rules () {
      return {
        required: value => !!value || this.$t('required_field')
      }
    },
    pendingRequestsCount () {
      return this.pendingReservations.length
    },
    cancellationRequestsCount () {
      return this.cancellationReservations.length
    },
    timeSlots () {
      const startHour = 7
      const endHour = 10
      const interval = 10
      const slots = new Set()

      for (let h = startHour; h <= endHour; h++) {
        for (let m = 0; m < 60; m += interval) {
          if (h === endHour && m > 0) break
          slots.add(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
        }
      }

      this.reservations.forEach(r => {
        if (r.hour) slots.add(r.hour.substring(0, 5))
      })

      return Array.from(slots).sort()
    },
    isWeekend () {
      if (!this.filterDate) return false
      const [year, month, day] = this.filterDate.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      const d = date.getDay()
      return d === 0 || d === 6
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    async initialize () {
      await this.getCourts()
      await this.getUsers()
      await this.getCaddies()
      await this.getRequests()
    },

    getReservationsBySlot (time, hole) {
      return this.reservations.filter(r => {
        const rTime = r.hour ? r.hour.substring(0, 5) : ''
        return rTime === time && r.start_hole === hole
      })
    },

    openDialogWithTime (time, hole) {
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedItem.date = this.filterDate
      this.editedItem.hour = time
      this.editedItem.start_hole = hole
      this.editedIndex = -1
      this.dialog = true
    },

    showReservationDetails (item) {
      this.selectedReservation = item
      this.detailsDialog = true
    },

    async getCourts () {
      try {
        const response = await this.apiCall('get', '/golf-courses/')
        this.courts = response.data
        if (this.courts.length > 0) {
          this.defaultItem.course_id = this.courts[0].id
        }
      } catch (error) {
        console.error('Error fetching courts:', error)
      }
    },

    async getUsers () {
      this.loadingUsers = true
      try {
        const response = await this.apiCall('get', '/residents/all')
        const residents = response.data.residents || []
        this.users = residents.map(resident => ({
          id: resident.idUser.id,
          full_name: `${resident.idUser.first_name} ${resident.idUser.last_name}`,
          email: resident.idUser.email,
          address: resident.address,
          ...resident.idUser
        }))
      } catch (error) {
        console.error('Error fetching users:', error)
        this.showSnackbar(this.$t('error_fetching_users'), 'error')
      } finally {
        this.loadingUsers = false
      }
    },

    async getCaddies () {
      try {
        const response = await this.apiCall('get', '/caddies/')
        this.caddies = response.data
      } catch (error) {
        console.error('Error fetching caddies:', error)
      }
    },

    async getReservationsForDate (date) {
      this.loading = true
      try {
        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${date}&end=${date}`)
        this.reservations = this.processReservations(response.data)
      } catch (error) {
        console.error('Error fetching reservations:', error)
        this.showSnackbar(this.$t('error_fetching_reservations'), 'error')
      } finally {
        this.loading = false
      }
    },

    async getMonthReservations (start, end) {
      if (!start || !end) return
      try {
        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${start}&end=${end}`)
        this.monthReservations = response.data
      } catch (error) {
        console.error('Error fetching month reservations:', error)
      }
    },

    setToday () {
      this.focus = ''
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    updateRange ({ start, end }) {
      this.fetchMonthReservations(start.date, end.date)
    },
    async fetchMonthReservations (start, end) {
      try {
        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${start}&end=${end}`)
        this.monthReservations = response.data
      } catch (error) {
        console.error('Error fetching month reservations:', error)
      }
    },
    getReservationCount (date) {
      return this.monthReservations.filter(r => r.date === date).length
    },
    openDayDialog (date) {
      const dateString = date.date || date
      this.filterDate = dateString
      this.getReservationsForDate(dateString)
      this.dayDialog = true
    },

    async getRequests () {
      this.loadingRequests = true
      try {
        const response = await this.apiCall('get', '/golf-reservations/')
        const processedData = this.processReservations(response.data)
        // Filter for status 1 (Pending) and 3 (Cancellation Requested)
        this.pendingReservations = processedData.filter(r => r.status === 1)
        this.cancellationReservations = processedData.filter(r => r.status === 3)
      } catch (error) {
        console.error('Error fetching requests:', error)
      } finally {
        this.loadingRequests = false
      }
    },

    onDateChange (date) {
      this.getReservationsForDate(date)
    },

    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      if (data) {
        config.data = data
      }

      return axios(config)
    },

    getCourseName (course) {
      if (!course) return ''
      if (typeof course === 'object') return course.name
      const court = this.courts.find(c => c.id === course)
      return court ? court.name : course
    },

    formatDate (date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },

    isPast (item) {
      const now = new Date()
      const resDate = new Date(`${item.date}T${item.hour}`)
      return resDate < now
    },

    isActionDisabled (item, action) {
      // Disable actions for cancelled, rejected, cancelled late, or no-show reservations
      if (item.status === 4 || item.status === 5 || item.status === 6 || item.status === 7) return true

      if (action === 'no_show') {
        return false
      }

      if (this.isPast(item)) return true
      if (item.status === 3 && action === 'edit') return true
      return false
    },

    getStatusColor (status) {
      const colors = {
        1: 'orange',
        2: 'green',
        3: 'purple',
        4: 'blue',
        5: 'grey',
        6: 'deep-orange',
        7: 'red darken-2'
      }
      return colors[status] || 'grey'
    },

    getStatusText (status) {
      const keys = {
        1: 'pending',
        2: 'confirmed',
        3: 'cancellation_requested',
        4: 'cancelled_plus_24h',
        5: 'rejected',
        6: 'cancelled_minus_24h',
        7: 'no_show'
      }
      return this.$t(keys[status] || 'unknown')
    },

    getStatusBaseColor (status) {
      const colors = {
        1: 'orange',
        2: 'green',
        3: 'purple',
        4: 'blue',
        5: 'grey',
        6: 'deep-orange',
        7: 'red'
      }
      return colors[status] || 'grey'
    },

    async changeStatus (item, newStatus) {
      try {
        await this.apiCall('patch', `/golf-reservations/${item.id}/`, { status: newStatus })

        this.getReservationsForDate(this.filterDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          this.fetchMonthReservations(firstDay, lastDay)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error updating status:', error)
        this.showSnackbar(this.$t('error_updating_status'), 'error')
      }
    },

    async handleCancellationRequest (item, action) {
      try {
        const response = await this.apiCall('post', `/golf-reservations/${item.id}/handle-cancellation-request/`, { action })
        // Show the message from the backend which includes strike information
        const message = response.data?.detail || this.$t('status_updated_success')
        this.showSnackbar(message, 'success')
        this.getReservationsForDate(this.filterDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          this.fetchMonthReservations(firstDay, lastDay)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error handling cancellation request:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_updating_status')
        this.showSnackbar(errorMsg, 'error')
      }
    },

    availableCaddies (currentValue) {
      const selectedIds = [this.mainPlayerCaddie, ...this.guestCaddies].filter(id => id)
      return this.caddies.filter(c => !selectedIds.includes(c.id) || c.id === currentValue)
    },

    onUserSelectionChange (val) {
      if (typeof val === 'object' && val !== null) {
        // Resident selected
        this.editedItem.user_id = val.id
        this.editedItem.guest_name = ''
      } else if (typeof val === 'string') {
        // Guest name typed
        this.editedItem.user_id = null
        this.editedItem.guest_name = val
      } else {
        // Cleared
        this.editedItem.user_id = null
        this.editedItem.guest_name = ''
      }
    },

    updateCaddieIds () {
      const ids = []
      if (this.mainPlayerCaddie) ids.push(this.mainPlayerCaddie)

      this.guestCaddies.forEach(id => {
        if (id) ids.push(id)
      })

      this.editedItem.caddie_ids = ids
    },

    addPlayer () {
      if (this.editedItem.invited_people.length >= 5) {
        this.showSnackbar(this.$t('max_players_reached'), 'warning')
        return
      }
      if (this.newPlayerName) {
        this.editedItem.invited_people.push(this.newPlayerName)
        this.guestCaddies.push(null)
        this.newPlayerName = ''
      }
    },

    removePlayer (index) {
      this.editedItem.invited_people.splice(index, 1)
      this.guestCaddies.splice(index, 1)
      this.updateCaddieIds()
    },

    openDialog () {
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedItem.date = this.filterDate
      this.editedIndex = -1
      this.selectedUser = null
      this.dialog = true
    },

    editItem (item) {
      this.editedIndex = this.reservations.indexOf(item)
      this.editedItem = Object.assign({}, item)
      if (!Array.isArray(this.editedItem.invited_people)) {
        this.editedItem.invited_people = []
      }
      this.editedItem.course_id = (item.course && typeof item.course === 'object') ? item.course.id : item.course
      this.editedItem.caddie_ids = item.caddies ? item.caddies.map(c => (typeof c === 'object' ? c.id : c)) : (item.caddie ? [item.caddie.id || item.caddie] : [])
      this.editedItem.user_id = (item.user && typeof item.user === 'object') ? item.user.id : item.user

      // Initialize caddie distribution
      this.mainPlayerCaddie = null
      this.guestCaddies = []

      if (this.editedItem.caddie_ids && this.editedItem.caddie_ids.length > 0) {
        // Assign first caddie to main player
        this.mainPlayerCaddie = this.editedItem.caddie_ids[0]

        // Assign remaining caddies to guests sequentially
        if (this.editedItem.caddie_ids.length > 1) {
          for (let i = 1; i < this.editedItem.caddie_ids.length; i++) {
            // Ensure array size matches invited people
            if (i - 1 < this.editedItem.invited_people.length) {
              this.$set(this.guestCaddies, i - 1, this.editedItem.caddie_ids[i])
            }
          }
        }
      }

      // Ensure guestCaddies has same length as invited_people
      while (this.guestCaddies.length < this.editedItem.invited_people.length) {
        this.guestCaddies.push(null)
      }

      // Initialize Smart Input
      if (this.editedItem.user_id) {
        // Try to find user in loaded users, otherwise we might need to fetch or just show ID (ideally we have the user object)
        // For now assuming users are loaded or we can find it.
        // If item.user is an object we can use it directly
        if (typeof item.user === 'object') {
          this.selectedUser = item.user
        } else {
          this.selectedUser = this.users.find(u => u.id === this.editedItem.user_id) || this.editedItem.user_id
        }
      } else if (this.editedItem.guest_name) {
        this.selectedUser = this.editedItem.guest_name
      } else {
        this.selectedUser = null
      }

      this.dialog = true
    },

    confirmCancel (item) {
      this.itemToCancel = item
      this.dialogCancel = true
    },

    async cancelItemConfirm () {
      if (this.itemToCancel) {
        try {
          await this.apiCall('delete', `/golf-reservations/${this.itemToCancel.id}/`)
          this.showSnackbar(this.$t('reservation_cancelled_success'), 'success')
          await this.getReservationsForDate(this.filterDate)
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          await this.fetchMonthReservations(firstDay, lastDay)
          await this.getRequests()
        } catch (error) {
          console.error('Error cancelling reservation:', error)
          this.showSnackbar(this.$t('error_cancelling_reservation'), 'error')
        } finally {
          this.closeCancel()
        }
      }
    },

    closeCancel () {
      this.dialogCancel = false
      this.$nextTick(() => {
        this.itemToCancel = null
      })
    },

    confirmNoShow (item) {
      this.itemToNoShow = item
      this.dialogNoShow = true
    },

    async noShowItemConfirm () {
      if (this.itemToNoShow) {
        try {
          // Use status 7 for No Show - this will automatically add a strike
          const payload = {
            status: 7
          }
          await this.apiCall('patch', `/golf-reservations/${this.itemToNoShow.id}/`, payload)
          this.showSnackbar(this.$t('no_show_marked_success'), 'success')
          await this.getReservationsForDate(this.filterDate)
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          await this.fetchMonthReservations(firstDay, lastDay)
          await this.getRequests()
        } catch (error) {
          console.error('Error marking no show:', error)
          const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_marking_no_show')
          this.showSnackbar(errorMsg, 'error')
        } finally {
          this.closeNoShow()
        }
      }
    },

    closeNoShow () {
      this.dialogNoShow = false
      this.$nextTick(() => {
        this.itemToNoShow = null
      })
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        this.$refs.form.resetValidation()
      })
    },

    validateTime () {
      const course = this.courts.find(c => c.id === this.editedItem.course_id)
      if (!course) return true

      const time = this.editedItem.hour
      if (!time) return true

      if (time < course.opening_time || time > course.closing_time) {
        this.showSnackbar(this.$t('error_msg_0') + ` (${course.opening_time} - ${course.closing_time})`, 'error')
        return false
      }
      return true
    },

    async save () {
      if (!this.$refs.form.validate()) return
      if (!this.validateTime()) return

      try {
        const payload = {
          user_id: this.editedItem.user_id,
          guest_name: this.editedItem.guest_name,
          course_id: this.editedItem.course_id,
          date: this.editedItem.date,
          hour: this.editedItem.hour,
          start_hole: this.editedItem.start_hole,
          hole_count: this.editedItem.hole_count,
          invited_people: this.editedItem.invited_people,
          caddie_ids: this.editedItem.caddie_ids,
          is_recurring: this.editedItem.is_recurring,
          end_date: this.editedItem.end_date,
          days_of_week: this.editedItem.days_of_week
        }

        if (this.editedIndex > -1) {
          // Edit
          await this.apiCall('patch', `/golf-reservations/${this.editedItem.id}/`, payload)
          this.showSnackbar(this.$t('reservation_updated_success'), 'success')
        } else {
          // Create
          if (!this.editedItem.user_id && this.editedItem.guest_name) {
            // Create for guest
            await this.apiCall('post', '/golf-reservations/create-for-guest/', payload)
          } else {
            // Create for resident
            await this.apiCall('post', '/golf-reservations/', payload)
          }
          this.showSnackbar(this.$t('reservation_created_success'), 'success')
        }

        this.close()
        this.getReservationsForDate(this.filterDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          this.fetchMonthReservations(firstDay, lastDay)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error saving reservation:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_saving_reservation')
        this.showSnackbar(errorMsg, 'error')
      }
    },

    showSnackbar (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    getUserName (user) {
      if (!user) return ''
      if (typeof user === 'object') {
        if (user.first_name || user.last_name) {
          return `${user.first_name || ''} ${user.last_name || ''}`.trim()
        }
        return user.email || ''
      }
      const found = this.users.find(u => u.id === user)
      return found ? found.full_name : user
    },

    processReservations (data) {
      return data.map(r => ({
        ...r,
        display_user: r.user ? this.getUserName(r.user) : (r.guest_name || 'Invitado')
      }))
    },

    getInitials (name) {
      if (!name) return ''
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getRecessTime (startTime) {
      if (!startTime) return ''
      const [h, m] = startTime.split(':').map(Number)
      let rh = h + 2
      let rm = m + 39
      if (rm >= 60) {
        rh += 1
        rm -= 60
      }
      return `${rh.toString().padStart(2, '0')}:${rm.toString().padStart(2, '0')}`
    },

    openNewCaddieDialog (context) {
      this.caddieCreationContext = context
      this.newCaddie = { name: '', caddie_type: null }
      if (this.$refs.formNewCaddie) {
        this.$refs.formNewCaddie.resetValidation()
      }
      this.dialogNewCaddie = true
    },

    closeNewCaddieDialog () {
      this.dialogNewCaddie = false
    },

    async saveNewCaddie () {
      if (!this.$refs.formNewCaddie.validate()) return

      this.loadingNewCaddie = true
      try {
        const response = await this.apiCall('post', '/caddies/', this.newCaddie)
        const createdCaddie = response.data
        this.caddies.push(createdCaddie)

        if (this.caddieCreationContext === -1) {
          this.mainPlayerCaddie = createdCaddie.id
        } else if (this.caddieCreationContext >= 0) {
          this.$set(this.guestCaddies, this.caddieCreationContext, createdCaddie.id)
        }
        this.updateCaddieIds()
        this.showSnackbar(this.$t('caddie_created_success') || 'Caddie creado exitosamente', 'success')
        this.closeNewCaddieDialog()
      } catch (error) {
        console.error('Error creating caddie:', error)
        this.showSnackbar(this.$t('error_creating_caddie') || 'Error al crear caddie', 'error')
      } finally {
        this.loadingNewCaddie = false
      }
    }
  }
}
</script>

<style scoped>
.tee-sheet-table th {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem !important;
}

.tee-row:hover td {
  background-color: #f5f5f5;
}

.cell-hover:hover .add-btn-container {
  opacity: 1 !important;
}

.add-btn-container:hover {
  background-color: rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.clickable-card {
  cursor: pointer;
}
</style>
